import * as React from 'react';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { Chip, IconButton, InputAdornment, TextField } from '@mui/material';
import { AppContext } from '../../App';

let MAX_TAG_LENGTH = 100;

export interface TagsProps {
  domId: string;
  addTag: (value: string) => void;
  removeTag: (value: string) => void;
  tags: string[];
  placeholder?: string;
  variant?: 'outlined' | 'filled' | 'standard';
  color?: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  cypressId?: string;
  isReadOnly?: boolean;
}

const Tags: React.FC<TagsProps> = ({
  addTag,
  removeTag,
  tags,
  domId = '',
  placeholder = 'New Tag',
  color = 'primary',
  variant = 'standard',
  cypressId,
  isReadOnly,
}) => {
  const [value, setValue] = React.useState('');
  const [errorMessage, setErrorMessage] = React.useState('');

  const { setToast } = React.useContext(AppContext);

  const handleAddTag = async () => {
    let newTag = value.replace(/\s+/g, ' ').trim();

    // Check that adding a tag won't exceed max
    // That the tag isn't empty
    // That the tag length isn't > max
    if (newTag.length === 0) {
      setErrorMessage('Tag cannot be empty.');
    } else if (newTag.length > MAX_TAG_LENGTH) {
      setErrorMessage(`Tag cannot be over ${MAX_TAG_LENGTH} characters.`);
    } else if (tags.find((label) => label.toLowerCase() === newTag.toLowerCase())) {
      setErrorMessage('Tag already exists.');
    } else {
      try {
        addTag(newTag);
        setTimeout(() => {
          setValue('');
          setErrorMessage('');
        }, 200);
      } catch (error) {
        console.log(error);
        setErrorMessage('Something went wrong.');
      }
    }
  };

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    if (errorMessage.length > 0) {
      setErrorMessage('');
    }
  };

  const endAdornment = (
    <InputAdornment position="end">
      <IconButton
        disabled={errorMessage.length > 0 || isReadOnly}
        data-cy="btn-add-new-tag"
        onClick={handleAddTag}
        data-testid="add-tag-button"
      >
        <AddCircleIcon />
      </IconButton>
    </InputAdornment>
  );

  return (
    <div style={{ display: 'inline' }}>
      <TextField
        id={domId}
        data-cy={cypressId ? cypressId : 'input-add-new-tag'}
        placeholder={placeholder}
        autoComplete="off"
        InputProps={{ endAdornment }}
        value={value}
        error={errorMessage.length > 0}
        disabled={isReadOnly}
        helperText={
          <span data-cy={`new-tag-helper-text-${cypressId ? cypressId : ''}`}>
            {errorMessage.length > 0 ? errorMessage : ''}
          </span>
        }
        onKeyPress={(e) => {
          if (e.key === 'Enter') {
            // prevents submit on Mark Field Modal
            e.preventDefault();
            e.stopPropagation();

            handleAddTag();
          }
        }}
        onChange={handleOnChange}
        variant={variant}
        color={color}
      />

      {tags.map((tag) => (
        <Chip
          key={tag}
          id={tag}
          data-cy={`tag-chip-${tag}`}
          label={tag}
          variant="outlined"
          color={color}
          style={{ margin: '0.25em 0.5em' }}
          disabled={isReadOnly}
          onClick={() => {
            navigator.clipboard.writeText(tag);
            setToast({
              variant: 'success',
              open: true,
              message: 'Copied',
            });
          }}
          onDelete={async () => {
            try {
              removeTag(tag);
              setValue('');
            } catch (error) {
              console.error(error);
            }
          }}
        />
      ))}
    </div>
  );
};

export default React.memo(Tags);
